import { InfoIcon } from '@/assets/icons/info-icons/info';
import { Banner } from '@/components/basicComponents/banner';
import Input from '@/components/basicComponents/input';
import { formatDollarInputValue, formatMaskedDollarInputValue } from '@/components/basicComponents/input/utils';
import Select from '@/components/basicComponents/select';
import { InvestmentVehicleDto } from '@/components/dealPipeline-page/modify/types';
import FooterModal from '@/components/settings-page/importData/footer';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import { PROPOSAL_TYPE } from '@/types/investments';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { updateWizardSteps, WizardProgressBar } from '../../../basicComponents/wizard/progressBar';

export interface ITransactionDetailsStepForm {
  amount: string;
  vehicle: string;
}

export const transactionDetailsStepDefaultValues: ITransactionDetailsStepForm = {
  amount: '',
  vehicle: ''
};

const validateAmountInputValue = (value: string, proposalType: PROPOSAL_TYPE, minimum: number) => {
  const amountValue = parseFloat(value);

  if (proposalType === PROPOSAL_TYPE.INITIAL) {
    if (amountValue < minimum) {
      return 'The initial investment amount must be equal or greater than the investment minimum.';
    }
  } else if (proposalType === PROPOSAL_TYPE.INCREASE) {
    if (amountValue <= 0) {
      return 'Any increase investment value must be greater than 0';
    }
  }

  return true;
};

interface TransactionDetailsStepProps {
  signaturePacketWizardSteps: { name: string; status: string }[];
  investmentVehicles: InvestmentVehicleDto[];
  transactionDetailsStepData: {
    committedCapital: number;
    proposalType: PROPOSAL_TYPE;
    minimum: number;
    investmentVehicleId: string;
  };
  saveTransactionDetailsData: (data: ITransactionDetailsStepForm) => Promise<void>;
  handleSignaturePacketWizardSteps: (steps: { name: string; status: string }[]) => void;
}

export const TransactionDetailsStep = ({
  signaturePacketWizardSteps,
  investmentVehicles,
  transactionDetailsStepData,
  saveTransactionDetailsData,
  handleSignaturePacketWizardSteps
}: TransactionDetailsStepProps) => {
  const { isMobile } = useResponsive();
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit
  } = useForm<ITransactionDetailsStepForm>({ defaultValues: transactionDetailsStepDefaultValues });

  const [vehicleTaxStatus, setVehicleTaxStatus] = useState('');

  useEffect(() => {
    reset((defaultValues) => ({
      ...defaultValues,
      amount: transactionDetailsStepData?.committedCapital?.toString(),
      vehicle: transactionDetailsStepData?.investmentVehicleId ?? ''
    }));

    if (transactionDetailsStepData?.investmentVehicleId) {
      const selectedVehicle = investmentVehicles.find((vehicle) => vehicle.id === transactionDetailsStepData?.investmentVehicleId);
      if (selectedVehicle && typeof selectedVehicle.taxExempt === 'boolean') {
        setVehicleTaxStatus(selectedVehicle.taxExempt ? 'Exempt' : 'Taxable');
      }
    }
  }, [transactionDetailsStepData]);

  return (
    <>
      <StepWrapper isMobile={isMobile}>
        <WizardProgressBar steps={signaturePacketWizardSteps} />
        <TwoColumnWrapper isMobile={isMobile}>
          <Controller
            name="amount"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Amount is required'
              },
              validate: (value) => validateAmountInputValue(value, transactionDetailsStepData.proposalType, transactionDetailsStepData.minimum)
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                label="Amount"
                value={value ? formatMaskedDollarInputValue(value) : ''}
                onChange={(e) => {
                  const formattedValue = formatDollarInputValue(e.target.value);
                  onChange(formattedValue);
                }}
                errorText={error?.message}
                withDollarSign
                size="md"
                isRequiredField
                description={transactionDetailsStepData.proposalType === 'Increase' ? '' : `Minimum: $${transactionDetailsStepData.minimum}`}
              />
            )}
          />
          <div>
            <Banner
              title=""
              description="Ensure amount meets minimum"
              icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
              bgColor={themeVar('layer.1')}
              color={themeVar('font.base')}
            />
          </div>
        </TwoColumnWrapper>
        <TwoColumnWrapper isMobile={isMobile}>
          <Controller
            name="vehicle"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Vehicle is required'
              }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                label="Vehicle"
                value={value}
                setValue={(newValue) => {
                  onChange(newValue);
                  const selectedVehicle = investmentVehicles.find((vehicle) => vehicle.id === newValue);
                  if (selectedVehicle && typeof selectedVehicle.taxExempt === 'boolean') {
                    setVehicleTaxStatus(selectedVehicle.taxExempt ? 'Exempt' : 'Taxable');
                    return;
                  }
                  setVehicleTaxStatus('');
                }}
                options={investmentVehicles?.map((vehicle) => ({ label: vehicle.name || vehicle.id, value: vehicle.id })) ?? []}
                errorText={error?.message}
                size="md"
                isRequiredField
                description={vehicleTaxStatus && `Vehicle Tax Status: ${vehicleTaxStatus}`}
              />
            )}
          />
          <div>
            <Banner
              title=""
              description="Select a vehicle matching this investor entity’s requirements."
              icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
              bgColor={themeVar('layer.1')}
              color={themeVar('font.base')}
            />
          </div>
        </TwoColumnWrapper>
      </StepWrapper>
      <FooterModal
        startOverButtonName="Back"
        startOver={() => updateWizardSteps('prev', signaturePacketWizardSteps, handleSignaturePacketWizardSteps)}
        confirmButton={{
          name: 'Save And Continue',
          onClick: handleSubmit(saveTransactionDetailsData),
          disabled: !!Object.keys(errors || {}).length
        }}
      />
    </>
  );
};

const StepWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 30px 0 150px 0;
  padding: ${({ isMobile }) => (isMobile ? '20px 30px' : '40px 60px')};
  background: ${themeVar('layer.1')};
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;
`;

const TwoColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  align-items: center;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '30px')};
`;
